import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { APP_BASE } from '@/lib/api';

const AUTH_STORAGE_KEY = 'sapori1989_admin_auth';

/*
✅ BACKEND URL ROBUSTO
evita /undefined/api
forza HTTPS
*/
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export const AuthProvider = ({ children }) => {

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /*
  ============================
  LOAD USER DA LOCALSTORAGE
  ============================
  */

  useEffect(() => {

    const loadUser = async () => {

      try {

        const stored = localStorage.getItem(AUTH_STORAGE_KEY);

        if (!stored) {
          setIsLoading(false);
          return;
        }

        const parsed = JSON.parse(stored);

        if (!parsed?.token) {
          setIsLoading(false);
          return;
        }

        const url = `${APP_BASE}/api/admin/me`;

        console.log('[AUTH] CHECK SESSION =>', url);

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${parsed.token}`
          }
        });

        if (response.ok) {

          const data = await response.json();

          setUser({
            ...data,
            token: parsed.token
          });

        } else {

          localStorage.removeItem(AUTH_STORAGE_KEY);

        }

      } catch (err) {

        console.error('[AUTH] loadUser error:', err);
        localStorage.removeItem(AUTH_STORAGE_KEY);

      } finally {

        setIsLoading(false);

      }

    };

    loadUser();

  }, []);

  /*
  ============================
  LOGIN
  ============================
  */

  const login = useCallback(async (email, password) => {

    setIsLoading(true);
    setError(null);

    try {

      const url = `${APP_BASE}/api/admin/login`;

      console.log('[AUTH] LOGIN URL =>', url);

      const response = await fetch(url, {

        method: 'POST',

        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },

        body: JSON.stringify({
          email,
          password
        })

      });

      const raw = await response.text();

      let data;

      try {

        data = raw ? JSON.parse(raw) : null;

      } catch {

        console.error('[AUTH] risposta NON JSON:', raw);

        throw new Error(
          'Risposta non valida dal server. Controlla rewrite o endpoint login'
        );
      }

      if (!response.ok) {

        const msg =
          data?.error ||
          data?.message ||
          'Credenziali non valide';

        throw new Error(msg);

      }

      const userData = {
        ...(data.user || {}),
        token: data.token
      };

      setUser(userData);

      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify(userData)
      );

      return { success: true };

    } catch (err) {

      setError(err.message);

      return {
        success: false,
        error: err.message
      };

    } finally {

      setIsLoading(false);

    }

  }, []);

  /*
  ============================
  LOGOUT
  ============================
  */

  const logout = useCallback(async () => {

    try {

      if (user?.token) {

        await fetch(`${APP_BASE}/api/admin/logout`, {

          method: 'POST',

          headers: {
            Authorization: `Bearer ${user.token}`
          }

        });

      }

    } catch (err) {

      console.error('[AUTH] logout error:', err);

    } finally {

      setUser(null);

      localStorage.removeItem(AUTH_STORAGE_KEY);

    }

  }, [user]);

  /*
  ============================
  CONTEXT VALUE
  ============================
  */

  const value = {

    user,
    token: user?.token,
    isLoading,
    isAuthenticated: !!user,
    error,

    login,
    logout

  };

  return (

    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>

  );

};
